/**
 * useModeDetection — web platform glue for the GPS soft-default (T12.6 D2, D5).
 *
 * The decision itself lives in the pure `detectMode` geofence
 * (`@shopista/shared/stores`). This hook is the platform half: it reads the
 * device position (Geolocation API), gathers the household home coords +
 * selected-store coords from the data already loaded for the session, calls
 * `detectMode`, and applies the result via `useSetAppMode` whenever the geofence
 * returns a non-null result. The mode is purely location-derived — a late GPS
 * fix always applies; there is no manual-override guard (T12.6 Reopen 2).
 *
 * Trigger split by platform (D2, amended 2026-07-04):
 *   - **Native (`Capacitor.isNativePlatform()`):** auto-detect runs on app entry
 *     directly through `deviceLocation`; native permission handling is owned by
 *     that acquisition seam. The WebView Permissions API is NOT trusted on
 *     native, so the banner keys off the typed acquisition outcome.
 *   - **Web (PWA / browser):** auto-detect stays silent. It runs only when the
 *     Permissions API already reports `granted`; it never auto-prompts on cold
 *     load (browsers penalize it). The banner (D6) is the web opt-in path.
 *
 * Outcome signal (D6, error-code discrimination amended T12.6.1). `locationWorking`
 * reflects whether location is actually WORKING (a usable fix resolved), not a
 * permission snapshot:
 *   - `null`  — undetermined: either before the entry attempt resolves, OR a
 *               transient failure (`POSITION_UNAVAILABLE` / `TIMEOUT`, codes 2/3)
 *               that stayed unresolved after the one low-accuracy retry (T12.6.1
 *               — an indoor/transient GPS blip must NOT read as "not working").
 *   - `true`  — a usable fix resolved (even if "elsewhere", not near a store/home).
 *   - `false` — a HARD signal that location isn't working: `PERMISSION_DENIED`
 *               (code 1), or (web) the Permissions API reports not-granted, or
 *               geolocation is unsupported. The GPS-needed banner and the
 *               contextual asks (Deals inline row, ModeSpine hint) all key off
 *               this exact value — they show while `false`, never while `null`.
 *
 * The acquisition module supplies a progressive, coarse-tolerant watch ladder.
 * A no-fix transient result remains `null` (undetermined), while denial is the
 * only permission failure that makes the signal `false`.
 *
 * Privacy (D2): coordinates never leave the device — the geofence is client-side.
 *
 * Fallback (D3): denied / unavailable / timeout / low-accuracy / no home coords
 * all fall through silently for the MODE. The opening mode then stays whatever
 * seeded the first render (`DEFAULT_APP_MODE`) — this hook simply doesn't act on
 * the mode; only the banner reacts to the not-working outcome.
 *
 * Lifecycle (D4): the auto-detect effect runs once per mounted session at entry.
 * There is no continuous or resume-driven re-detection — the logo press
 * (`requestDetection`) is the mid-session recompute trigger.
 *
 * Home-coords gate + cross-surface signal (T12.6.1). The persistent ambient
 * banner now ALSO requires the household to have no home coordinates (postal
 * setup fully covers store discovery + deals, so a postal-only household is
 * never nagged app-wide) — `hasHomeCoords` on the return value carries that.
 * GPS still stays wanted for a postal-located household (it powers the in-store
 * Planning→Shopping switch and the Deals filter), so two contextual asks replace
 * the banner there: the Deals inline row and the ModeSpine post-manual-switch
 * hint. AppShell stays the SOLE caller of this hook (D5) and prop-drills
 * `locationWorking` / `hasHomeCoords` / `requestDetection` into its direct
 * children (LocationNeededBanner, ModeSpine). The Deals page is NOT an AppShell
 * child (it's routed content), so it can't receive props this way without a
 * second hook call (which would double the OS prompt / `getCurrentPosition`) —
 * `useLocationSignal()` below is a plain subscribe-only reader of a module-level
 * broadcast the one live hook instance publishes to, so Deals reads the exact
 * same outcome with no second entry-trigger side effect.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Capacitor } from '@capacitor/core';
import {
  detectLocationContext,
  detectMode,
  type Coords,
  type DevicePosition,
  type SelectedStore,
} from '@shopista/shared/stores';
import {
  useCurrentHousehold,
  useSelectedStores,
  useStores,
} from '@shopista/shared/api/hooks';
import { useSetAppMode } from './useAppMode';
import { useSetLocationContext } from './LocationContext';
import { acquireDeviceLocation } from './deviceLocation';

/**
 * Stable module-level default platform probe. Defined once (not a fresh arrow per
 * render) so a no-arg caller never feeds a new identity into the hook, and so the
 * only `@capacitor/core` call in this module stays behind an injectable seam that
 * tests can replace without mocking the module. Mirrors useNativeAppUpdate.
 */
const defaultIsNative = (): boolean => Capacitor.isNativePlatform();

/** Coerce a `pgNumeric` (string | number | null | undefined) to a finite number or null. */
function toNumber(value: unknown): number | null {
  if (value == null) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Coerce a household/store's raw lat/lng fields to a `Coords` pair or `null`.
 * Exported (T12.6.1) so surfaces that are not AppShell's direct children — the
 * Deals inline row — can compute the same "does this household have home
 * coordinates" gate the hook uses internally (`hasHomeCoords`), without a
 * second `useCurrentHousehold`-shaped parse living in two places.
 */
export function coordsOf(
  latitude: unknown,
  longitude: unknown
): Coords | null {
  const lat = toNumber(latitude);
  const lng = toNumber(longitude);
  if (lat === null || lng === null) return null;
  return { latitude: lat, longitude: lng };
}

export interface ModeDetectionStatus {
  /**
   * True when the explicit opt-in affordance should be shown — i.e. the browser
   * supports geolocation and permission is not already `granted` (web), so
   * auto-detect did not silently run. Retained for the existing consumers; the
   * banner keys off `locationWorking` instead.
   */
  canOptIn: boolean;
  /**
   * Whether location is actually WORKING — an OUTCOME, not a permission snapshot
   * (D6). `null` until the entry attempt resolves; `true` once a usable fix
   * lands; `false` when the attempt errors (denied / unavailable / timeout) or
   * (web) permission is not granted. The GPS-needed banner shows while `false`.
   * Keyed off the `getCurrentPosition` outcome so an OS-level revoke that the
   * WebView Permissions API still reports as `granted` is caught (D2).
   */
  locationWorking: boolean | null;
  /**
   * Whether the household has home coordinates set (T12.6.1) — the gate the
   * persistent ambient banner now also requires (`locationWorking === false`
   * AND `hasHomeCoords === false`) before it shows; a postal-located household
   * never sees the app-wide nag. Same underlying parse (`coordsOf`) the
   * geofence itself uses.
   */
  hasHomeCoords: boolean;
  /**
   * Trigger detection on explicit user action (the banner's enable action). This
   * is the only path that may raise the native permission prompt on web (D2).
   * Safe to call regardless of permission state; it no-ops if geolocation is
   * unsupported.
   */
  requestDetection: () => void;
}

// ---------------------------------------------------------------------------
// Cross-surface location signal (T12.6.1) — see the module docblock's
// "Home-coords gate + cross-surface signal" section. AppShell's sole
// `useModeDetection()` instance PUBLISHES its `locationWorking` +
// `requestDetection` here on every change; `useLocationSignal()` only
// SUBSCRIBES + reads (`useSyncExternalStore`) — it never calls
// `useModeDetection()` itself, so it never runs a second entry-trigger side
// effect (no second `getCurrentPosition` / OS prompt).
// ---------------------------------------------------------------------------

export interface LocationSignal {
  /** Mirrors `ModeDetectionStatus.locationWorking` — see that doc above. */
  locationWorking: boolean | null;
  /** Mirrors `ModeDetectionStatus.requestDetection`. */
  requestDetection: () => void;
}

const NOOP_REQUEST_DETECTION = (): void => {
  /* no-op until AppShell's hook instance has published a real one */
};

const EMPTY_LOCATION_SIGNAL: LocationSignal = {
  locationWorking: null,
  requestDetection: NOOP_REQUEST_DETECTION,
};

let publishedLocationSignal: LocationSignal = EMPTY_LOCATION_SIGNAL;
const locationSignalListeners = new Set<() => void>();

function getPublishedLocationSignal(): LocationSignal {
  return publishedLocationSignal;
}

function publishLocationSignal(next: LocationSignal): void {
  publishedLocationSignal = next;
  locationSignalListeners.forEach((listener) => listener());
}

function subscribeLocationSignal(listener: () => void): () => void {
  locationSignalListeners.add(listener);
  return () => {
    locationSignalListeners.delete(listener);
  };
}

/**
 * Read the outcome + enable action AppShell's sole `useModeDetection()`
 * instance publishes, without running the hook (and its entry-trigger side
 * effect) again. For surfaces that are not AppShell's direct children (the
 * Deals inline row, T12.6.1) but still need the same signal.
 */
export function useLocationSignal(): LocationSignal {
  return useSyncExternalStore(subscribeLocationSignal, getPublishedLocationSignal);
}

/**
 * Test-only — publish a signal directly without mounting the hook (so a
 * consumer test, e.g. DealsPage's, can seed `useLocationSignal()` without
 * driving the full geolocation/permissions mock surface). Also used to reset
 * the module-level broadcast between test cases.
 */
export function publishLocationSignalForTest(signal: LocationSignal): void {
  publishLocationSignal(signal);
}

/** Test-only — clear the module-level broadcast back to its empty default. */
export function resetLocationSignalForTest(): void {
  publishedLocationSignal = EMPTY_LOCATION_SIGNAL;
  locationSignalListeners.clear();
}

export interface UseModeDetectionOptions {
  /**
   * Platform probe. Defaults to the real `Capacitor.isNativePlatform()`. Tests
   * inject `() => true` to drive the native auto-detect-on-entry branch or
   * `() => false` to drive the web permission-gated branch.
   */
  isNative?: () => boolean;
}

export function useModeDetection(
  options: UseModeDetectionOptions = {}
): ModeDetectionStatus {
  const isNative = options.isNative ?? defaultIsNative;

  const setAppMode = useSetAppMode();
  const setLocationContext = useSetLocationContext();
  const householdQuery = useCurrentHousehold();
  const selectedQuery = useSelectedStores();
  const storesQuery = useStores();

  const [canOptIn, setCanOptIn] = useState(false);
  const [locationWorking, setLocationWorking] = useState<boolean | null>(null);
  // Guard so the one-shot auto-detect fires at most once per mounted session.
  const autoDetectedRef = useRef(false);
  // `isNative` may be a fresh arrow each render; read the live value from a ref
  // so the entry effect can depend on nothing and still branch on the latest.
  const isNativeRef = useRef(isNative);
  isNativeRef.current = isNative;

  const household = householdQuery.data;
  const home = coordsOf(household?.latitude, household?.longitude);
  // T12.6.1 — the ambient banner's home-coords gate; also exported for
  // `useLocationSignal` consumers (the Deals row) that compute the same gate
  // via `coordsOf` directly (no second household-shaped parse).
  const hasHomeCoords = home !== null;

  // Selected stores: the selected-stores endpoint returns ids only, so
  // cross-reference against the full store list (which carries lat/lng). Only
  // selected stores are geofence candidates (D1). Each candidate retains its id
  // (T39) so the location context can name the matched store — the id the Deals
  // store filter keys on.
  const selectedIds = selectedQuery.data?.storeIds ?? [];
  const allStores = storesQuery.data?.stores ?? [];
  const selectedStores: SelectedStore[] = allStores
    .filter((s) => selectedIds.includes(s.id))
    .map((s) => {
      const coords = coordsOf(s.latitude, s.longitude);
      return coords ? { id: s.id, ...coords } : null;
    })
    .filter((s): s is SelectedStore => s !== null);

  // Run the geofence against a resolved position. The location context is ALWAYS
  // updated (T39) — it drives Deals' store awareness. The mode is purely
  // location-derived: a non-null `detectMode` result is always applied (no
  // override guard, T12.6 Reopen 2). A `null` result ("elsewhere" / low-accuracy
  // / missing coords) leaves the mode unchanged by simply not calling setAppMode.
  const applyFromPosition = useCallback(
    (position: DevicePosition) => {
      const input = { position, home, selectedStores };
      // Always record where we are — session-only, never persisted (AC6).
      setLocationContext(detectLocationContext(input));
      const result = detectMode(input);
      if (result) {
        setAppMode(result.mode);
      }
    },
    // `home` / `selectedStores` are recomputed each render from query data;
    // the callback closes over the latest values, which is what we want for a
    // one-shot triggered after data load.
    [home, selectedStores, setAppMode, setLocationContext]
  );

  // Acquire through the shared device-local ladder. A cache hit seeds the
  // geofence immediately; a partial timeout remains a usable fix, while a
  // no-fix transient failure deliberately preserves T12.6.1's `null` signal.
  // a transient error). A usable fix marks location WORKING (D6) — even if the
  // geofence places us "elsewhere". This is the sole source of truth for the
  // outcome signal, so an OS-level revoke that the Permissions API still calls
  // `granted` is caught here rather than trusting the (stale) permission
  // snapshot (D2).
  //
  // Error-code discrimination (T12.6.1 — the banner over-fired on a granted-
  // but-indoor-timeout, since every error used to mark not-working):
  //   - `PERMISSION_DENIED` (1) — a hard signal; mark NOT working immediately,
  //     no retry (the OS won't produce a different answer without a Settings
  //     change).
  //   - `POSITION_UNAVAILABLE` / `TIMEOUT` (2 / 3) — transient; retry ONCE at
  const runDetection = useCallback(() => {
    const applyOutcome = (outcome: Awaited<ReturnType<typeof acquireDeviceLocation>>): void => {
      const fix = outcome.kind === 'ok' ? outcome.fix : outcome.kind === 'timeout' ? outcome.partial : undefined;
      if (fix) {
        setLocationWorking(true);
        setCanOptIn(false);
        applyFromPosition({ latitude: fix.latitude, longitude: fix.longitude, accuracy: fix.accuracy });
        return;
      }
      if (outcome.kind === 'denied' || outcome.kind === 'unsupported') setLocationWorking(false);
    };
    void acquireDeviceLocation({
      purpose: 'mode-detection',
      maxAgeMs: 10 * 60 * 1000,
      accuracyTargetM: 500,
      deadlineMs: 20_000,
      onRefresh: applyOutcome,
    }).then(applyOutcome);
  }, [applyFromPosition]);

  // One-shot auto-detect at entry (D2). Native delegates permission + location
  // handling to the shared acquisition seam.
  // Web: silent, ONLY when the Permissions API already reports `granted`;
  // otherwise mark not-working (the banner is the web opt-in path) and surface
  // the opt-in affordance.
  useEffect(() => {
    if (autoDetectedRef.current) return;
    // Wait until the data needed for the geofence has loaded; without home or
    // any selected store there is nothing to detect against.
    if (householdQuery.isLoading || selectedQuery.isLoading || storesQuery.isLoading) {
      return;
    }

    // Native: auto-detect on entry directly. Drop the `granted`-only gate — the
    // call raises the OS prompt on first launch and re-requests after an
    // OS-level revoke; the banner reacts to the resulting outcome, not a
    // permission snapshot the WebView can report stale (D2).
    if (isNativeRef.current()) {
      autoDetectedRef.current = true;
      setCanOptIn(false);
      runDetection();
      return;
    }

    // Web path — permission-gated silent auto-detect (never a cold-load prompt).
    const permissions = navigator.permissions;
    if (!permissions || typeof permissions.query !== 'function') {
      // No Permissions API — never auto-prompt on cold load; expose the opt-in
      // and show the banner (location isn't working until the user opts in).
      setCanOptIn(true);
      setLocationWorking(false);
      return;
    }

    let cancelled = false;
    void permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((status) => {
        if (cancelled) return;
        if (status.state === 'granted') {
          // Already granted → silent auto-detect. `runDetection` sets
          // `locationWorking` from the actual fix outcome.
          setCanOptIn(false);
          if (!autoDetectedRef.current) {
            autoDetectedRef.current = true;
            runDetection();
          }
        } else {
          // prompt / denied → do nothing automatically; offer the opt-in and
          // show the banner (location is not working on web until granted).
          setCanOptIn(true);
          setLocationWorking(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCanOptIn(true);
          setLocationWorking(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    householdQuery.isLoading,
    selectedQuery.isLoading,
    storesQuery.isLoading,
    runDetection,
  ]);

  const requestDetection = useCallback(() => {
    autoDetectedRef.current = true;
    setCanOptIn(false);
    runDetection();
  }, [runDetection]);

  // T12.6.1 — publish this instance's outcome + enable action so surfaces that
  // are not AppShell's direct children (the Deals inline row) can read the same
  // signal via `useLocationSignal()` without calling this hook a second time.
  useEffect(() => {
    publishLocationSignal({ locationWorking, requestDetection });
  }, [locationWorking, requestDetection]);

  return { canOptIn, locationWorking, hasHomeCoords, requestDetection };
}
